import React from 'react';
import { TestSubsection } from './TestSection';
import { Database, Shield, DollarSign, AlertTriangle, Clock, Mail } from 'lucide-react';

/**
 * Data Integrity & Audit Section
 * Displays results from the new data integrity test suites (Jan 2026)
 */
export const DataIntegrityAuditSection = ({ testResults }) => {
  // Cross-Service Availability Tests
  const crossServiceTests = testResults?.crossServiceAvailability || [];
  
  // Data Integrity Tests
  const dataIntegrityTests = testResults?.dataIntegrity || [];
  
  // Financials P&L Tests
  const financialsTests = testResults?.financialsData || [];
  
  // Timezone Consistency Tests
  const timezoneTests = testResults?.timezoneConsistency || [];
  
  // Email Data Integrity Tests
  const emailTests = testResults?.emailDataIntegrity || [];
  
  // Calculate summary stats
  const allTests = [...crossServiceTests, ...dataIntegrityTests, ...financialsTests, ...timezoneTests, ...emailTests];
  const passedCount = allTests.filter(t => t?.status === 'pass' || t?.status === 'passed').length;
  const failedCount = allTests.filter(t => t?.status === 'fail' || t?.status === 'failed').length;
  const warningCount = allTests.filter(t => t?.status === 'warning').length;
  
  // Don't render if no tests
  if (allTests.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg font-bold">Data Integrity & Audit</h2>
          <span className="text-xs text-gray-500">
            ({passedCount}/{allTests.length} passed)
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {failedCount > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
              {failedCount} failed
            </span>
          )}
          {warningCount > 0 && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
              {warningCount} warnings
            </span>
          )}
        </div>
      </div>
      
      {/* Test Subsections */}
      <div className="space-y-2">
        {/* Cross-Service Availability */}
        {crossServiceTests.length > 0 && (
          <TestSubsection
            title="Cross-Service Availability"
            icon={Shield}
            iconColor="text-purple-500"
            results={crossServiceTests}
          />
        )}
        
        {/* Data Integrity */}
        {dataIntegrityTests.length > 0 && (
          <TestSubsection
            title="Data Integrity"
            icon={AlertTriangle}
            iconColor="text-orange-500"
            results={dataIntegrityTests}
          />
        )}
        
        {/* Financials P&L */}
        {financialsTests.length > 0 && (
          <TestSubsection
            title="Financials / P&L"
            icon={DollarSign}
            iconColor="text-green-500"
            results={financialsTests}
          />
        )}
        
        {/* Timezone Consistency */}
        {timezoneTests.length > 0 && (
          <TestSubsection
            title="Timezone Consistency"
            icon={Clock}
            iconColor="text-blue-500"
            results={timezoneTests}
          />
        )}
        
        {/* Email Data Integrity */}
        {emailTests.length > 0 && (
          <TestSubsection
            title="Email Data Integrity"
            icon={Mail}
            iconColor="text-red-500"
            results={emailTests}
          />
        )}
      </div>
    </div>
  );
};

export default DataIntegrityAuditSection;
